import Div from "@jumbo/shared/Div/Div";
import { Grid, CircularProgress } from "@mui/material";
import {
  getDSOKOrdersForStatusScreen,
  getBEUrl,
} from "app/services/api/management";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSocket } from "app/providers/SocketProvider";
import { ReadyIconAnimated, InProgressIconAnimated } from "./AnimatedIcons";
import styled, { createGlobalStyle } from "styled-components";

// Global styles for CSS variables
const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  
  :root {
    --bg-color: #0d1a2d;
    --card-in-progress-bg: #1a2b4b;
    --card-ready-bg: #103833;
    --text-color: #e0e6f1;
    --text-heading-color: #ffffff;
    --number-bg-color: #384d72;
    --number-ready-bg-color: #1e5e54;
    --number-text-color: #ffffff;
    --font-family: "Montserrat", "Roboto", sans-serif;
  }

  body {
    background-color: var(--bg-color);
    color: var(--text-color);
    font-family: var(--font-family);
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    min-height: 100vh;
  }
`;

// Styled components for the card styles
const StyledCard = styled.div`
  padding: 2rem;
  border-radius: 1rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const CardInProgress = styled(StyledCard)`
  background-color: var(--card-in-progress-bg, #189ab1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 400px;
  margin-left: 30px;
  margin-right: 30px;
  margin-top: 50px;

  /* Responsive for 1080x1920 screens (portrait orientation) */
  @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
    height: 750px;
    margin-left: 40px;
    margin-right: 40px;
    margin-top: 80px;
    padding: 3rem;
  }

  /* Responsive for 1920x1080 screens (landscape orientation) */
  @media (min-width: 1920px) and (max-height: 1080px) {
    height: 430px;
    margin-left: 25px;
    margin-right: 25px;
    margin-top: 40px;
    padding: 1.5rem;
  }
`;

const CardReady = styled(StyledCard)`
  background-color: var(--card-ready-bg, #18b12a);
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 400px;
  margin-left: 30px;
  margin-right: 30px;

  /* Responsive for 1080x1920 screens (portrait orientation) */
  @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
    height: 750px;
    margin-left: 40px;
    margin-right: 40px;
    padding: 3rem;
  }

  /* Responsive for 1920x1080 screens (landscape orientation) */
  @media (min-width: 1920px) and (max-height: 1080px) {
    height: 430px;
    margin-left: 25px;
    margin-right: 25px;
    padding: 1.5rem;
  }
`;

// Card header styled component
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 1.5rem;

  svg {
    width: 40px;
    height: 40px;
  }

  h2 {
    font-size: 40px;
    font-weight: 700;
    padding-left: 20px;
    color: var(--text-heading-color);
    margin: 0;

    @media (max-width: 768px) {
      font-size: 24px;
    }

    /* Responsive for 1080x1920 screens (portrait orientation) */
    @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
      font-size: 60px;
      padding-left: 30px;
    }

    /* Responsive for 1920x1080 screens (landscape orientation) */
    @media (min-width: 1920px) and (max-height: 1080px) {
      font-size: 35px;
      padding-left: 15px;
    }
  }

  /* Responsive for 1080x1920 screens (portrait orientation) */
  @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
    gap: 25px;
    margin-bottom: 2rem;

    svg {
      width: 60px;
      height: 60px;
    }
  }

  /* Responsive for 1920x1080 screens (landscape orientation) */
  @media (min-width: 1920px) and (max-height: 1080px) {
    gap: 12px;
    margin-bottom: 1rem;

    svg {
      width: 35px;
      height: 35px;
    }
  }
`;

// Numbers container styled component
const NumbersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  /* Responsive for 1080x1920 screens (portrait orientation) */
  @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
    gap: 1rem;
  }

  /* Responsive for 1920x1080 screens (landscape orientation) */
  @media (min-width: 1920px) and (max-height: 1080px) {
    gap: 0.75rem;
  }
`;

// Number bubble styled component
const NumberBubble = styled.div`
  background-color: var(--number-bg-color);
  color: var(--number-text-color);
  font-size: 3rem;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  min-width: 80px;
  text-align: center;
  transition: transform 0.2s ease-in-out;

  /* Responsive for 1080x1920 screens (portrait orientation) */
  @media (min-width: 1080px) and (max-width: 1920px) and (orientation: portrait) {
    font-size: 3.5rem;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    min-width: 100px;
  }

  /* Responsive for 1920x1080 screens (landscape orientation) */
  @media (min-width: 1920px) and (max-height: 1080px) {
    font-size: 2.5rem;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    min-width: 70px;
  }
`;

// Ready number bubble styled component
const ReadyNumberBubble = styled(NumberBubble)`
  background-color: var(--number-ready-bg-color);
  animation: shake 10s infinite;

  @keyframes shake {
    0%,
    90%,
    100% {
      transform: translateX(0);
    }
    92% {
      transform: translateX(-5px);
    }
    94% {
      transform: translateX(5px);
    }
    96% {
      transform: translateX(-5px);
    }
    98% {
      transform: translateX(5px);
    }
  }
`;

const OrderStatusScreen = () => {
  const { socket, connect, subscribe, unsubscribe } = useSocket();
  const { locationCode, languages, timeInterval, type, customerCode, env } =
    useParams();

  const { t, i18n } = useTranslation();
  const languagesList = languages.split(",");
  const [currentLanguage, setCurrentLanguage] = useState(languagesList[0]);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadOrders, setLoadOrders] = useState(true);
  const settings = getScreenSettings(type);

  // WebSocket connection
  useEffect(() => {
    let baseUrl = "";
    switch (env) {
      case "prod":
        baseUrl = `https://be.gastrokiosk.online`;
        break;
      case "stage":
        baseUrl = `https://staging.gastrokiosk.online`;
        break;
      case "dev":
        baseUrl = `http://localhost:8080`;
        break;
      default:
        baseUrl = `https://be.gastrokiosk.online`;
        break;
    }
    const baseURL = baseUrl.replace(/\/$/, ""); // Remove trailing slash
    const wsURL = baseURL
      .replace(/^https:\/\//, "wss://")
      .replace(/^http:\/\//, "ws://"); // Convert to appropriate WebSocket protocol
    connect(wsURL);
  }, [connect]);

  // WebSocket subscription for order preparation status changes
  useEffect(() => {
    if (socket?.active && customerCode) {
      const channel = `admin/dsokOrderPreparationStatus/${customerCode}`;
      subscribe(channel, handleUpdate);

      return () => {
        unsubscribe(channel, handleUpdate);
      };
    }
  }, [socket?.active, customerCode, subscribe, unsubscribe]);

  const handleUpdate = (message) => {
    if (message === "refresh") {
      setLoadOrders(true);
    }
  };

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
    if (languagesList.length > 1) {
      const changeLanguage = () => {
        const currentIndex = languagesList.indexOf(currentLanguage);
        const nextIndex = (currentIndex + 1) % languagesList.length;
        setCurrentLanguage(languagesList[nextIndex]);
        i18n.changeLanguage(languagesList[nextIndex]);
      };
      const interval = setInterval(changeLanguage, timeInterval || 15000); // Change language every 15 seconds or get settings from url
      return () => clearInterval(interval);
    }
  }, [currentLanguage, i18n]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getDSOKOrdersForStatusScreen(locationCode, env);
        setData(result);
        setLoadOrders(false);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loadOrders]);

  // Fallback polling — periodicky aktualizuje obsah (bez reloadu stránky), keby WS realtime
  // vypadol (socket.io transport býva nespoľahlivý). Read-only display, bez lokálnych akcií.
  useEffect(() => {
    const POLL_MS = 10000;
    const timer = setInterval(() => setLoadOrders(true), POLL_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    document.body.classList.add("call-screen-body");
    return () => {
      document.body.classList.remove("call-screen-body");
    };
  }, []);

  if (loading) {
    return (
      <Div
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress size={60} />
      </Div>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }
  const containerStyle = {
    backgroundImage: `url(${
      window.innerWidth > window.innerHeight
        ? settings.backgroundImage
        : settings.backgroundImagePortrait
    })`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <Div style={{ height: "100vh", width: "100vw", overflow: "hidden" }}>
      <GlobalStyles />
      <Div style={containerStyle}>
        <Grid container spacing={4} alignItems={"stretch"}>
          <Grid item xs={12} md={12}>
            <CardInProgress>
              <CardHeader>
                <InProgressIconAnimated color="#e0e6f1" fontSize="4rem" />
                <h2>{t("pages.easyPayCallScreen.inProgressBox")}</h2>
              </CardHeader>
              <NumbersContainer>
                {data?.inProgressOrders?.map((order, index) => (
                  <NumberBubble key={index}>{order?.orderNumber}</NumberBubble>
                ))}
              </NumbersContainer>
            </CardInProgress>
          </Grid>
          <Grid item xs={12} md={12}>
            <CardReady>
              <CardHeader>
                {settings.iconReady === "ReadyIconAnimated" ? (
                  <ReadyIconAnimated color="#ffffff" fontSize="4rem" />
                ) : (
                  <div style={{ width: "4rem", height: "4rem" }}></div>
                )}
                <h2>{t("pages.easyPayCallScreen.readyBox")}</h2>
              </CardHeader>
              <NumbersContainer>
                {data?.readyOrders?.map((order, index) => (
                  <ReadyNumberBubble key={index}>
                    {order?.orderNumber}
                  </ReadyNumberBubble>
                ))}
              </NumbersContainer>
            </CardReady>
          </Grid>
        </Grid>
      </Div>
    </Div>
  );
};

/**
 *
 * @param {*} type
 * @returns
 */
const getScreenSettings = (type) => {
  switch (type) {
    case "restaurant":
      return {
        backgroundImage: "/images/dsok/orderScreen/bg-restaurant.jpg",
        backgroundImagePortrait:
          "/images/dsok/orderScreen/bg-restaurant-portrait.jpg",
        iconInprogress: "FastFoodIconAnimated",
        iconReady: "ReadyIconAnimated",
      };
    default:
      return {
        backgroundImage: "/images/dsok/orderScreen/bg-restaurant.jpg",
        backgroundImagePortrait:
          "/images/dsok/orderScreen/bg-restaurant-portrait.jpg",
        iconInprogress: "FastFoodIconAnimated",
        iconReady: "ReadyIconAnimated",
      };
  }
};
export default OrderStatusScreen;
